import { dateToPlainDateString, plainDateToZonedDateTime } from '../../util/date-utilts';
import { getFileExtension } from '../../util/file-utils';
import { ACCEPTED_EXTENSIONS, MAX_FILE_SIZE_BYTES } from './documentHub.constants';
import { CompanyDocument, DocType, DocumentStatus } from './documentHub.schema';

/**
 * Statuses that still need the user's attention: uploaded/detecting (processing)
 * and detected (awaiting confirmation). Confirmed + failed docs drop out. Drives
 * the processing banner and the set of docs shown in the review flow.
 */
const PENDING_REVIEW_STATUSES = new Set<DocumentStatus>([
  DocumentStatus.Uploaded,
  DocumentStatus.Detecting,
  DocumentStatus.Detected,
]);

export function isPendingReview(doc: CompanyDocument): boolean {
  return PENDING_REVIEW_STATUSES.has(doc.status);
}

/**
 * True once detection has started (the doc is past `uploaded`). These docs are
 * shown in the Review step — reviewing is gated per-document, so a doc still
 * `detecting` surfaces in Review rather than waiting for the whole batch.
 */
export function isReviewable(doc: CompanyDocument): boolean {
  return doc.status !== DocumentStatus.Uploaded;
}

/**
 * Trigger a browser download of `url` (a presigned S3 GET URL) as `fileName` via
 * a transient anchor click. The BE already sets content-disposition, so the
 * `download` attribute is best-effort (ignored cross-origin) — the file name
 * still comes through from the server. Mirrors the idiom used elsewhere in the
 * app (CompanyDocumentSection, RoundDocumentList).
 */
export function triggerBrowserDownload(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export interface FileValidationResult {
  /** Files that passed type + size validation. */
  validFiles: File[];
  /** Files that failed, with a human-readable reason. */
  rejectedFiles: { file: File; reason: string }[];
}

/**
 * Partition dropped/selected files into valid and rejected sets by extension,
 * size, and duplicate file name — a file is rejected when its name matches an
 * existing document (`existingFileNames`) or another file earlier in the same
 * selection. Name comparison is case-insensitive.
 */
export function validateFiles(files: File[], existingFileNames: Iterable<string> = []): FileValidationResult {
  const validFiles: File[] = [];
  const rejectedFiles: { file: File; reason: string }[] = [];
  const existing = new Set(Array.from(existingFileNames, (name) => name.toLowerCase()));
  const seenInBatch = new Set<string>();

  for (const file of files) {
    const ext = getFileExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      rejectedFiles.push({ file, reason: 'Unsupported file type' });
      continue;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      rejectedFiles.push({ file, reason: 'File exceeds the 100MB limit' });
      continue;
    }
    const nameKey = file.name.toLowerCase();
    if (existing.has(nameKey)) {
      rejectedFiles.push({ file, reason: 'A document with this name already exists' });
      continue;
    }
    if (seenInBatch.has(nameKey)) {
      rejectedFiles.push({ file, reason: 'Duplicate file name in this upload' });
      continue;
    }
    seenInBatch.add(nameKey);
    validFiles.push(file);
  }

  return { validFiles, rejectedFiles };
}

/** Doc types that deliver to FullSuite via the data-request pipeline. */
const FULLSUITE_DOC_TYPES = [DocType.Financials, DocType.CapTable, DocType.BoardDeck];

/** Grid label for FullSuite delivery; null = not applicable (deal docs, unconfirmed rows). */
export function deliveryStatusFor(
  doc: Pick<CompanyDocument, 'docType' | 'status' | 'period' | 'confirmSyncedAt' | 'companyEnrolled'>
): string | null {
  if (!FULLSUITE_DOC_TYPES.includes(doc.docType) || doc.status !== DocumentStatus.Confirmed) {
    return null;
  }
  if (doc.confirmSyncedAt !== null) {
    return 'Delivered';
  }
  // Cap tables deliver without a period (SB attaches them to the current quarter).
  if (doc.period === null && doc.docType !== DocType.CapTable) {
    return 'Needs period';
  }
  if (!doc.companyEnrolled) {
    return 'Company not enrolled';
  }
  return 'Delivering…';
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** The period as the calendar control holds it; a legacy free-text period has no date to show. */
export function periodPickerValue(period: string | null | undefined): Date | null {
  return period && ISO_DATE_PREFIX.test(period) ? plainDateToZonedDateTime(period) : null;
}

/** The calendar control's date as the stored period (plain YYYY-MM-DD); an invalid or cleared date is null. */
export function periodFromPicker(date: Date | null): string | null {
  return date && !Number.isNaN(date.valueOf()) ? dateToPlainDateString(date) : null;
}
